import type { TrapetsLookupInput } from "@repo/shared/types";
import { useActingRepresentatives } from "@/lib/hooks/use-client-detail";
import {
  useSearchAndSaveTrapetsLookup,
  useTrapetsLookupFlowInstanceValue,
} from "@/lib/hooks/use-flow-instances/use-trapets-lookup";
import type { TrapetsLookupSnapshot } from "../trapets-lookup/trapets-lookup-widget";
import { TrapetsLookupWidget } from "../trapets-lookup/trapets-lookup-widget";

type WizardTrapetsLookupProps = {
  instanceId: string;
  /** Needed to resolve the acting representative for a Company/Group. */
  clientId: string | null;
  clientType: "Person" | "Company" | "Group";
  clientName: string | null;
  clientCountry: string | null;
  clientSSN: string | null;
  clientDateOfBirth: string | null;
  clientOrgId: string | null;
  disabled: boolean;
  style?: "none" | "border" | "box";
  title?: string;
  description?: string;
  language: string;
  hasValidationError?: boolean;
};

export function WizardTrapetsLookup({
  instanceId,
  clientId,
  clientType,
  clientName,
  clientCountry,
  clientSSN,
  clientDateOfBirth,
  clientOrgId,
  disabled,
  style,
  title,
  description,
  language,
  hasValidationError,
}: WizardTrapetsLookupProps) {
  const {
    inputData,
    calculatedData,
    isLoading: valueLoading,
  } = useTrapetsLookupFlowInstanceValue(instanceId);
  const searchAndSave = useSearchAndSaveTrapetsLookup(instanceId);

  // QL-21 follow-up — PEP applies to a natural person, so a Company or Group is
  // screened through its registered representative. Resolved here to PREFILL the
  // representative half of the form; the advisor can change it, and saving the
  // search writes it back to Related parties so the two cannot drift.
  const isCompanyLike = clientType === "Company" || clientType === "Group";
  const { representatives, isLoading: repLoading } = useActingRepresentatives(
    isCompanyLike ? clientId : null,
  );

  const handleSearch = async (input: TrapetsLookupInput): Promise<TrapetsLookupSnapshot> => {
    const result = await searchAndSave.mutateAsync(input);
    if (!result.success || !result.inputData || !result.calculatedData) {
      throw new Error(result.error || "Search failed");
    }
    const parsed = JSON.parse(result.inputData);
    const calculated = JSON.parse(result.calculatedData);
    return {
      clientType: parsed.clientType,
      input: {
        name: parsed.name,
        country: parsed.country,
        ssn: parsed.ssn,
        ssnWithPrefix: parsed.ssnWithPrefix,
        birthDate: parsed.birthDate,
        orgId: parsed.orgId,
        representatives: parsed.representatives,
      },
      ...calculated,
    };
  };

  if (valueLoading || (isCompanyLike && repLoading)) return null;

  return (
    <TrapetsLookupWidget
      clientType={clientType}
      clientName={clientName}
      clientCountry={clientCountry}
      clientSSN={clientSSN}
      clientDateOfBirth={clientDateOfBirth}
      clientOrgId={clientOrgId}
      inputData={inputData}
      calculatedData={calculatedData}
      language={language}
      onSearch={handleSearch}
      representatives={representatives}
      disabled={disabled}
      style={style}
      title={title}
      description={description}
      hasValidationError={hasValidationError}
    />
  );
}
